export type MentionCandidate = {
  id: string;
  display_name: string | null;
  email: string | null;
  label: string;
};

export type CommentMention = {
  id: string | null;
  label: string;
};

export function mentionLabel(candidate: {
  display_name?: string | null;
  displayName?: string | null;
  email?: string | null;
}): string {
  const email = candidate.email?.trim() || '';
  if (email) return email;
  const name =
    candidate.display_name?.trim() || candidate.displayName?.trim() || '';
  if (name) return name;
  return 'Collaborator';
}

/** Prefer email for people lists; never fall back to raw user ids. */
export function personLabel(candidate: {
  display_name?: string | null;
  displayName?: string | null;
  email?: string | null;
}): string {
  return mentionLabel(candidate);
}

export function normalizeMentionCandidate(
  row: {
    user_id?: string;
    userId?: string;
    id?: string;
    display_name?: string | null;
    displayName?: string | null;
    email?: string | null;
  } | null,
): MentionCandidate | null {
  if (!row) return null;
  const id = row.user_id || row.userId || row.id || null;
  if (!id) return null;
  const display_name =
    row.display_name?.trim() || row.displayName?.trim() || null;
  const email = row.email?.trim() || null;
  return {
    id,
    display_name,
    email,
    label: mentionLabel({ display_name, email }),
  };
}

export function filterMentionCandidates(
  candidates: MentionCandidate[],
  query: string,
  { limit = 50 }: { limit?: number } = {},
): MentionCandidate[] {
  const list = (candidates || [])
    .map((c) => normalizeMentionCandidate(c))
    .filter((c): c is MentionCandidate => Boolean(c));
  const q = String(query || '')
    .trim()
    .toLowerCase();
  const filtered = q
    ? list.filter((c) => {
        if (c.label.toLowerCase().includes(q)) return true;
        if (c.email?.toLowerCase().includes(q)) return true;
        if (c.display_name?.toLowerCase().includes(q)) {
          return true;
        }
        return false;
      })
    : list;
  if (q) {
    filtered.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      const aPref = aLabel.startsWith(q) ? 0 : 1;
      const bPref = bLabel.startsWith(q) ? 0 : 1;
      if (aPref !== bPref) return aPref - bPref;
      return aLabel.localeCompare(bLabel);
    });
  }
  return filtered.slice(0, limit);
}

/** Open @-query just before caret in a plain string. */
export function readOpenMention(
  text: string,
  caret: number,
): { from: number; to: number; query: string } | null {
  const before = String(text || '').slice(0, Math.max(0, caret || 0));
  const match = /(^|[\s([{])@([^\s@]*)$/.exec(before);
  if (!match) return null;
  const query = match[2] || '';
  const atIndex = before.length - query.length - 1;
  return { from: atIndex, to: caret, query };
}

export function mentionToken(label: string): string {
  return `@${label}`;
}

export type MentionTextSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; id: string | null; label: string; value: string };

/** Split plain text into text / mention segments using known mention labels. */
export function segmentMentionText(
  body: string,
  mentions: CommentMention[] = [],
): MentionTextSegment[] {
  const text = String(body || '');
  const list = (mentions || [])
    .map((m) => ({
      id: m.id || null,
      label: String(m.label || '').trim(),
    }))
    .filter((m) => m.label)
    .sort((a, b) => b.label.length - a.label.length);

  if (!text || !list.length) return [{ type: 'text', value: text }];

  const parts: MentionTextSegment[] = [];
  let i = 0;
  while (i < text.length) {
    let hit: { id: string | null; label: string; token: string } | null = null;
    if (text[i] === '@') {
      for (const m of list) {
        const token = mentionToken(m.label);
        if (text.startsWith(token, i)) {
          const after = text[i + token.length];
          if (after && !/[\s,.;:!?)\]}]/.test(after)) continue;
          hit = { ...m, token };
          break;
        }
      }
    }
    if (hit) {
      parts.push({
        type: 'mention',
        id: hit.id,
        label: hit.label,
        value: hit.token,
      });
      i += hit.token.length;
      continue;
    }
    const start = i;
    i += 1;
    while (i < text.length) {
      if (text[i] === '@') break;
      i += 1;
    }
    parts.push({ type: 'text', value: text.slice(start, i) });
  }
  return parts;
}

export function mentionsStillInBody(
  body: string,
  mentions: CommentMention[] = [],
): CommentMention[] {
  const text = String(body || '');
  return (mentions || []).filter((m) => {
    const label = String(m.label || '').trim();
    if (!label) return false;
    const token = mentionToken(label);
    let idx = text.indexOf(token);
    while (idx >= 0) {
      const after = text[idx + token.length];
      if (!after || /[\s,.;:!?)\]}]/.test(after)) return true;
      idx = text.indexOf(token, idx + 1);
    }
    return false;
  });
}

export function renderMentionBody(
  container: HTMLElement,
  body: string,
  mentions: CommentMention[] = [],
) {
  container.replaceChildren();
  for (const part of segmentMentionText(body, mentions)) {
    if (part.type === 'mention') {
      const span = document.createElement('span');
      span.className = 'sn-comment-mention';
      if (part.id) span.dataset.userId = part.id;
      span.textContent = part.value;
      container.appendChild(span);
    } else {
      container.appendChild(document.createTextNode(part.value));
    }
  }
}
